// Contrast guard for app-icon tiles.
//
// Every generated app carries a `primaryColor` chosen by the model — it
// can land anywhere on the spectrum, including pale yellows and mints
// where a white initial is illegible. `readableInk` picks black or white
// to sit on a given background, using the same HSL conversion the theme
// engine uses (theme-tokens.ts), so the choice is consistent app-wide.

import { hexToHSL } from '@vibeshell/shared';

/**
 * Pick a foreground ink (#000 / #fff) that reads on `hex`.
 *
 * Uses perceived luminance rather than raw HSL lightness: the human eye
 * weights green far above blue, so a vivid blue (high-ish L) still needs
 * white ink while a vivid yellow needs black. Falls back to white for any
 * unparseable input — white-on-brand-pink is the product default.
 */
export function readableInk(hex: string | null | undefined): '#000000' | '#FFFFFF' {
  if (!hex || !/^#?[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(hex)) return '#FFFFFF';
  const normalized = hex.replace('#', '');
  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized;
  const r = parseInt(expanded.slice(0, 2), 16) / 255;
  const g = parseInt(expanded.slice(2, 4), 16) / 255;
  const b = parseInt(expanded.slice(4, 6), 16) / 255;
  // Rec. 601 perceived luminance — cheap and good enough for an icon tile.
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 0.62 ? '#000000' : '#FFFFFF';
}

/**
 * A `primaryColor` validator + fallback. Returns the color if it is a
 * well-formed 6-digit hex, otherwise the supplied fallback (default: the
 * brand pink). Centralises the regex that was copy-pasted across the
 * app-tile call sites.
 */
export function safeHex(hex: string | null | undefined, fallback = '#F4477E'): string {
  return hex && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : fallback;
}

// `hexToHSL` is re-exported so call sites that need the full HSL (e.g. a
// derived hover shade) can import it from one place alongside the guards.
export { hexToHSL };
